import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Leaf, Sprout, Truck, Wallet, Menu, X, MapPin, MessageCircle,
  ArrowUpRight, Flower2, ShoppingBag, Sparkles, Scissors,
} from "lucide-react";
import { WHATSAPP_NUMBER, wa, MESSAGES, IMAGES } from "@/config";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { fetchPublicConfig } from "@/api";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { AdsSection } from "@/components/AdsSection";
import { CouponsSection } from "@/components/CouponsSection";
import { AdminLogin } from "@/admin/AdminLogin";
import { AdminDashboard } from "@/admin/AdminDashboard";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Products", href: "#products" },
  { label: "Services", href: "#services" },
  { label: "Before & After", href: "#before-after" },
  { label: "Contact", href: "#contact" },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, dark }) => (
  <span className={`text-xs sm:text-sm uppercase tracking-[0.28em] font-bold ${dark ? "text-[#9db89d]" : "text-[var(--forest-2)]"}`}>
    {children}
  </span>
);

const WhatsAppBtn = ({ message, children, testid, variant = "primary", className = "" }) => {
  const base = "inline-flex items-center justify-center gap-2 rounded-full px-8 h-12 sm:h-14 text-xs sm:text-sm uppercase tracking-widest font-bold transition-[background-color,color,transform,box-shadow] duration-300 hover:scale-[1.03] active:scale-95";
  const styles = {
    primary: "bg-[var(--forest)] text-[var(--cream)] hover:bg-[var(--forest-2)] ambient",
    light: "bg-white text-[var(--forest)] border border-[var(--border)] hover:bg-[var(--beige)]",
    green: "bg-[#25D366] text-white hover:brightness-105 ambient",
    outline: "border border-white/40 text-white hover:bg-white hover:text-[var(--forest)]",
  };
  return (
    <a href={wa(message)} target="_blank" rel="noopener noreferrer" data-testid={testid} className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </a>
  );
};

// ---------- HEADER ----------
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header data-testid="site-header" className={`transition-[background-color,box-shadow,border-color] duration-500 ${scrolled ? "bg-[var(--cream)]/85 backdrop-blur-xl border-b border-[var(--border)]/60" : "bg-transparent border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#home" data-testid="logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Earth Nest Organics logo" className="h-9 w-9 object-contain" />
          <span className="font-accent text-lg sm:text-xl font-extrabold tracking-tight text-[var(--forest)]">EARTH NEST ORGANICS</span>
        </a>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`} className="text-sm font-medium text-[var(--ink)] hover:text-[var(--forest-2)] transition-colors duration-200">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <WhatsAppBtn message={MESSAGES.general} testid="header-whatsapp" variant="primary" className="hidden sm:inline-flex !px-6 !h-11">
            Order on WhatsApp
          </WhatsAppBtn>
          <button data-testid="menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border)] bg-white/70 text-[var(--forest)]">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} data-testid="mobile-menu" className="lg:hidden bg-[var(--cream)] border-b border-[var(--border)] px-4 py-6 space-y-1">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block rounded-xl px-4 py-3 text-base font-medium text-[var(--ink)] hover:bg-[var(--beige)]">
              {n.label}
            </a>
          ))}
          <WhatsAppBtn message={MESSAGES.general} testid="mobile-menu-whatsapp" variant="green" className="w-full mt-3">
            Order on WhatsApp
          </WhatsAppBtn>
        </motion.div>
      )}
    </header>
  );
};

// ---------- HERO ----------
const heroLines = ["Healthy Plants.", "Natural Care."];
const benefits = [
  { icon: Sprout, label: "Natural Organic Manure" },
  { icon: Flower2, label: "Plant Care Services" },
  { icon: Truck, label: "Zero Doorstep Delivery Fee" },
  { icon: Wallet, label: "Full COD Available" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  return (
    <section ref={ref} id="home" className="relative pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] min-h-[78vh] sm:min-h-[82vh] flex items-end ambient-lg">
          <motion.img style={{ y, scale }} src={IMAGES.hero} alt="Premium home garden in Dehradun" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
          <div className="relative z-10 p-6 sm:p-12 lg:p-16 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-xs sm:text-sm uppercase tracking-[0.28em] font-bold text-white/85">Dehradun, Uttarakhand · Natural Care for Your Plants</span>
            </motion.div>
            <h1 className="font-serif-display text-[var(--cream)] mt-4 text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight font-medium">
              {heroLines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.16, 1, 0.3, 1] }} className="block">
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }} className="mt-5 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed">
              Natural Cow Dung Organic Manure & Garden Care Services in Dehradun.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }} className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <WhatsAppBtn message={MESSAGES.general} testid="hero-order-whatsapp" variant="green">
                <MessageCircle className="h-4 w-4" /> Order on WhatsApp
              </WhatsAppBtn>
              <a href="#products" data-testid="hero-view-products" className="inline-flex items-center justify-center gap-2 rounded-full px-8 h-12 sm:h-14 text-xs sm:text-sm uppercase tracking-widest font-bold border border-white/40 text-white hover:bg-white hover:text-[var(--forest)] transition-[background-color,color] duration-300">
                View Products
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {benefits.map((b) => (
                <div key={b.label} className="flex items-center gap-2.5 rounded-2xl bg-white/12 backdrop-blur-md border border-white/15 px-4 py-3">
                  <b.icon className="h-5 w-5 text-[#a8d5a8] shrink-0" strokeWidth={1.8} />
                  <span className="text-xs sm:text-sm font-medium text-white leading-tight">{b.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- MARQUEE ----------
const Marquee = () => {
  const items = ["100% Natural", "Chemical Free", "Delivered to Your Door", "Dehradun Local", "Cow Dung Manure", "Cash on Delivery"];
  const row = [...items, ...items];
  return (
    <div className="bg-[var(--beige)] border-y border-[var(--border)] py-5 overflow-hidden">
      <div className="marquee-track">
        {row.map((t, i) => (
          <span key={i} className="font-serif-display italic text-2xl sm:text-4xl text-[var(--forest)] mx-8 flex items-center gap-8">
            {t} <Leaf className="h-5 w-5 text-[var(--moss)]" />
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------- PRODUCTS ----------
const DEFAULT_PRODUCTS = [
  { key: "p4", size: "4 KG", price: "99", desc: "Perfect for home pots", offer_text: "", popular: false },
  { key: "p10", size: "10 KG", price: "199", desc: "Ideal for home & kitchen gardens", offer_text: "", popular: true },
  { key: "p15", size: "15 KG", price: "299", desc: "Best for larger gardens", offer_text: "", popular: false },
];

const Products = ({ items }) => {
  const list = items && items.length ? items : DEFAULT_PRODUCTS;
  return (
    <section id="products" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <Overline>Natural Organic Manure</Overline>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
            Natural Organic Manure
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)]">Natural care for your pots, plants and garden.</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {list.map((p, i) => {
            const testid = `product-${p.key}`;
            const msg = `I want to order ${p.size} Earth Nest Organics manure for ₹${p.price}.`;
            return (
              <Reveal key={p.key} delay={i * 0.1}>
                <div data-testid={testid} className={`relative flex flex-col h-full rounded-3xl border p-6 sm:p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 ${p.popular ? "bg-[var(--forest)] border-[var(--forest)] ambient-lg" : "bg-white border-[var(--border)] ambient hover:ambient-lg"}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-[#d9b64e] px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[var(--forest)] whitespace-nowrap">
                      <Sparkles className="h-3.5 w-3.5" /> Most Popular
                    </span>
                  )}
                  {p.offer_text ? (
                    <span data-testid={`${testid}-offer`} className="absolute top-4 left-4 z-10 rounded-full bg-red-600 text-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest">{p.offer_text}</span>
                  ) : null}
                  <div className={`aspect-square rounded-2xl overflow-hidden mb-6 ${p.popular ? "bg-white/10" : "bg-[var(--beige)]"}`}>
                    <img src={IMAGES.pack} alt={`${p.size} organic manure`} className="w-full h-full object-cover" />
                  </div>
                  <div className={`text-sm font-bold uppercase tracking-widest ${p.popular ? "text-[#a8d5a8]" : "text-[var(--forest-2)]"}`}>{p.size}</div>
                  <div className={`font-serif-display text-5xl mt-1 ${p.popular ? "text-white" : "text-[var(--forest)]"}`}>₹{p.price}</div>
                  <p className={`mt-2 text-sm flex-1 ${p.popular ? "text-white/70" : "text-[var(--muted)]"}`}>{p.desc}</p>
                  <WhatsAppBtn message={msg} testid={`${testid}-order`} variant={p.popular ? "green" : "primary"} className="w-full mt-6">
                    Order Now
                  </WhatsAppBtn>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------- PRODUCT VISUAL ----------
const ProductVisual = () => (
  <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center rounded-[2.5rem] bg-[var(--beige)] border border-[var(--border)] p-6 sm:p-12 lg:p-16 overflow-hidden">
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden bg-white ambient-lg">
          <img src={IMAGES.pack} alt="Earth Nest Organics manure packaging" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Overline>The Product</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
          Clean packaging. <br /> Honest manure.
        </h2>
        <p className="mt-5 text-lg text-[var(--muted)] leading-relaxed">
          Our natural cow dung organic manure comes in a clean, modern pack — locally supplied and fresh. Simple, natural nourishment for your pots and garden beds.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          {["Natural Cow Dung", "Locally Sourced", "For Pots & Gardens"].map((t) => (
            <span key={t} className="rounded-full bg-white border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--forest)]">{t}</span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

// ---------- WHY CHOOSE US ----------
const reasons = [
  { icon: Sprout, title: "Natural Organic Manure", text: "Simple, natural cow dung manure for healthier soil and plants." },
  { icon: Flower2, title: "For Pots & Gardens", text: "Right for home pots, kitchen gardens and terrace beds." },
  { icon: Leaf, title: "Local & Fresh Supply", text: "Sourced locally in Dehradun and delivered fresh." },
  { icon: Truck, title: "Zero Doorstep Delivery Fee", text: "Doorstep delivery within Dehradun at ₹0 charge." },
  { icon: Wallet, title: "Full COD Available", text: "Pay comfortably with full cash on delivery." },
];

const WhyChooseUs = () => (
  <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[var(--forest)] text-[var(--cream)]">
    <div className="max-w-7xl mx-auto">
      <Reveal className="max-w-2xl">
        <Overline dark>Why Choose Us</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3">
          A gardening brand you can trust.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
        {reasons.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.07} className={i < 2 ? "md:col-span-3" : "md:col-span-2"}>
            <div className="h-full rounded-3xl bg-white/[0.06] border border-white/10 p-7 sm:p-8 backdrop-blur-sm transition-colors duration-300 hover:bg-white/[0.1]">
              <div className="flex items-center gap-3">
                <span className="font-accent text-sm font-bold text-[#a8d5a8]">0{i + 1}</span>
                <r.icon className="h-6 w-6 text-[#a8d5a8]" strokeWidth={1.6} />
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-5">{r.title}</h3>
              <p className="mt-2 text-white/70 leading-relaxed">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ---------- SERVICES ----------
const Services = () => (
  <section id="services" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <Reveal className="max-w-2xl">
        <Overline>Garden Care Services</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
          Professional plant care.
        </h2>
      </Reveal>
      <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Plant Feeding */}
        <Reveal>
          <div data-testid="service-feeding" className="flex flex-col h-full rounded-3xl bg-white border border-[var(--border)] overflow-hidden ambient">
            <div className="relative h-56 sm:h-64 overflow-hidden">
              <img src={IMAGES.feeding} alt="Plant feeding service" className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[var(--forest)]">Service 01</span>
            </div>
            <div className="p-7 sm:p-9 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-[var(--forest-2)]"><Sprout className="h-5 w-5" /><span className="text-sm font-bold uppercase tracking-widest">Plant Feeding Service</span></div>
              <h3 className="font-serif-display text-3xl mt-3 text-[var(--forest)]">Feeding & applying manure to your plants</h3>
              <p className="mt-3 text-[var(--muted)] leading-relaxed">
                Aapke existing plants aur pots mein hum khad properly feed / apply karenge.
              </p>
              <div className="mt-5 rounded-2xl bg-[var(--beige)] border border-[var(--border)] p-4 text-sm text-[var(--forest)]">
                <strong>Note:</strong> This is only a manure feeding / application service. We are <strong>not planting new plants</strong>.
              </div>
              <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
                <div><span className="font-serif-display text-4xl text-[var(--forest)]">₹15</span><span className="text-[var(--muted)] font-medium"> / kg</span></div>
                <WhatsAppBtn message={MESSAGES.feeding} testid="service-feeding-book" variant="primary">Book Feeding Service</WhatsAppBtn>
              </div>
            </div>
          </div>
        </Reveal>
        {/* Weeding */}
        <Reveal delay={0.1}>
          <div data-testid="service-weeding" className="flex flex-col h-full rounded-3xl bg-white border border-[var(--border)] overflow-hidden ambient">
            <div className="relative h-56 sm:h-64 overflow-hidden">
              <img src={IMAGES.weeding} alt="Garden weeding nulai service" className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[var(--forest)]">Service 02</span>
            </div>
            <div className="p-7 sm:p-9 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-[var(--forest-2)]"><Scissors className="h-5 w-5" /><span className="text-sm font-bold uppercase tracking-widest">Garden Weeding / Nulai Service</span></div>
              <h3 className="font-serif-display text-3xl mt-3 text-[var(--forest)]">Clean up weeds & unwanted grass</h3>
              <p className="mt-3 text-[var(--muted)] leading-relaxed">
                Plants ke aas-paas ki unwanted ghaas aur weeds ko khodkar properly saaf karna.
              </p>
              <div className="mt-5 flex gap-3">
                <div className="flex-1 rounded-2xl bg-[var(--beige)] border border-[var(--border)] p-4 text-center">
                  <div className="font-serif-display text-3xl text-[var(--forest)]">₹10</div><div className="text-xs uppercase tracking-widest text-[var(--muted)] font-bold">per pot</div>
                </div>
                <div className="flex-1 rounded-2xl bg-[var(--beige)] border border-[var(--border)] p-4 text-center">
                  <div className="font-serif-display text-3xl text-[var(--forest)]">₹8</div><div className="text-xs uppercase tracking-widest text-[var(--muted)] font-bold">per sq. ft.</div>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <WhatsAppBtn message={MESSAGES.weeding} testid="service-weeding-book" variant="primary">Book Weeding Service</WhatsAppBtn>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// ---------- BEFORE & AFTER ----------
const BeforeAfter = () => (
  <section id="before-after" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[var(--beige)]">
    <div className="max-w-6xl mx-auto">
      <Reveal className="text-center max-w-2xl mx-auto">
        <Overline>The Transformation</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
          Before <span className="text-[var(--moss)]">→</span> After
        </h2>
        <p className="mt-4 text-lg text-[var(--muted)]">Thodi si Nulai, Garden ka pura look change. 🌿</p>
      </Reveal>
      <Reveal delay={0.1} className="mt-12">
        <BeforeAfterSlider />
        <p className="text-center text-sm text-[var(--muted)] mt-4">Drag the handle to see the difference.</p>
      </Reveal>
    </div>
  </section>
);

// ---------- INSPIRATION ----------
const Inspiration = () => {
  const imgs = [IMAGES.insp1, IMAGES.insp2, IMAGES.after, IMAGES.insp3, IMAGES.insp4, IMAGES.hero];
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <Overline>Inspiration</Overline>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
            Imagine Your Garden Like This 🌿
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)]">A little inspiration for greener, calmer spaces at home.</p>
        </Reveal>
        <div className="mt-12 columns-2 lg:columns-3 gap-4 sm:gap-6 [column-fill:_balance]">
          {imgs.map((src, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="mb-4 sm:mb-6 break-inside-avoid">
              <div className="overflow-hidden rounded-3xl border border-[var(--border)] ambient group">
                <img src={src} alt="Garden inspiration" className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 2 === 0 ? "aspect-[3/4]" : "aspect-square"}`} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- HOW IT WORKS ----------
const steps = [
  { n: "01", title: "Choose", text: "Select manure or a garden-care service." },
  { n: "02", title: "WhatsApp", text: "Send your order or service request." },
  { n: "03", title: "Delivery / Visit", text: "We deliver the manure or visit for the service." },
  { n: "04", title: "Enjoy", text: "Healthy plants and a cleaner garden." },
];

const HowItWorks = () => (
  <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[var(--forest)] text-[var(--cream)]">
    <div className="max-w-7xl mx-auto">
      <Reveal className="max-w-2xl">
        <Overline dark>How It Works</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3">Simple, in four steps.</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-white/10 bg-white/[0.05] p-8">
              <div className="font-serif-display text-6xl text-[#a8d5a8]/60">{s.n}</div>
              <h3 className="font-serif-display text-2xl mt-4">{s.title}</h3>
              <p className="mt-2 text-white/70 leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ---------- DELIVERY BANNER ----------
const DeliveryBanner = () => (
  <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-5">
      {[
        { icon: Truck, title: "Zero Doorstep Delivery Fee", text: "Dehradun mein doorstep delivery charge ₹0." },
        { icon: Wallet, title: "Full Cash on Delivery", text: "Pay comfortably when your order reaches your door." },
      ].map((b, i) => (
        <Reveal key={b.title} delay={i * 0.1}>
          <div className="flex items-center gap-5 rounded-3xl bg-[var(--beige)] border border-[var(--border)] p-8 h-full">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[var(--forest)] text-[var(--cream)]">
              <b.icon className="h-8 w-8" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[var(--forest)]">{b.title}</h3>
              <p className="mt-1 text-[var(--muted)]">{b.text}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

// ---------- FINAL CTA ----------
const FinalCTA = () => (
  <section className="py-8 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[2.5rem] ambient-lg">
      <img src={IMAGES.insp2} alt="Lush garden" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[var(--forest)]/85" />
      <div className="relative z-10 px-6 py-20 sm:py-28 text-center text-[var(--cream)]">
        <Reveal>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Ready to Give Your Plants Natural Care?
          </h2>
          <p className="mt-5 text-lg text-white/80 max-w-xl mx-auto">
            Order Earth Nest Organics manure or book our plant-care services.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <WhatsAppBtn message={MESSAGES.orderManure} testid="cta-order-manure" variant="green"><ShoppingBag className="h-4 w-4" /> Order Manure</WhatsAppBtn>
            <WhatsAppBtn message={MESSAGES.bookService} testid="cta-book-service" variant="outline">Book a Service</WhatsAppBtn>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// ---------- CONTACT ----------
const Contact = () => (
  <section id="contact" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
    <div className="max-w-4xl mx-auto text-center">
      <Reveal>
        <Overline>Get in touch</Overline>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 text-[var(--forest)]">
          Contact Earth Nest Organics
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <div className="flex items-center justify-center gap-3 rounded-2xl bg-[var(--beige)] border border-[var(--border)] px-6 py-4 text-[var(--forest)]">
            <MapPin className="h-5 w-5" /> <span className="font-medium">Dehradun, Uttarakhand</span>
          </div>
          <div className="flex items-center justify-center gap-3 rounded-2xl bg-[var(--beige)] border border-[var(--border)] px-6 py-4 text-[var(--forest)]">
            <MessageCircle className="h-5 w-5" /> <span className="font-medium">WhatsApp: +{WHATSAPP_NUMBER}</span>
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <WhatsAppBtn message={MESSAGES.general} testid="contact-whatsapp" variant="green"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</WhatsAppBtn>
        </div>
      </Reveal>
    </div>
  </section>
);

// ---------- FOOTER ----------
const Footer = () => (
  <footer className="bg-[var(--forest)] text-[var(--cream)] px-4 sm:px-6 lg:px-8 pt-20 pb-10">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-2 gap-10 pb-12 border-b border-white/10">
        <div>
          <div className="flex items-center gap-2.5"><span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--cream)]"><img src="/logo.png" alt="Earth Nest Organics logo" className="h-8 w-8 object-contain" /></span><span className="font-accent text-xl font-extrabold tracking-tight">EARTH NEST ORGANICS</span></div>
          <p className="font-serif-display italic text-2xl sm:text-3xl mt-5 text-white/90 max-w-md">Natural Care for Your Plants</p>
        </div>
        <div className="md:justify-self-end flex flex-wrap gap-x-10 gap-y-3">
          {[...NAV, { label: "WhatsApp", href: wa(MESSAGES.general) }].map((n) => (
            <a key={n.label} href={n.href} target={n.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm text-white/70 hover:text-white transition-colors duration-200">{n.label}</a>
          ))}
        </div>
      </div>
      <div className="pt-8 text-sm text-white/50">© 2026 Earth Nest Organics. All Rights Reserved.</div>
    </div>
  </footer>
);

// ---------- FLOATING WHATSAPP ----------
const FloatingWhatsApp = () => (
  <a href={wa(MESSAGES.general)} target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp" aria-label="Chat on WhatsApp"
    className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-4 pr-5 h-14 shadow-xl hover:scale-110 transition-transform duration-300">
    <MessageCircle className="h-6 w-6" />
    <span className="hidden sm:inline text-sm font-bold">WhatsApp</span>
  </a>
);

const PublicSite = () => {
  const [config, setConfig] = useState({ announcement: null, products: [], ads: [], coupons: [] });
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    fetchPublicConfig().then(setConfig).catch(() => {});
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App noise-overlay bg-[var(--cream)]">
      <div className="fixed top-0 inset-x-0 z-50">
        <AnnouncementBar data={config.announcement} />
        <Header />
      </div>
      <Hero />
      <Marquee />
      <Products items={config.products} />
      <CouponsSection coupons={config.coupons} />
      <ProductVisual />
      <WhyChooseUs />
      <Services />
      <BeforeAfter />
      <Inspiration />
      <AdsSection ads={config.ads} />
      <HowItWorks />
      <DeliveryBanner />
      <FinalCTA />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicSite />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
